import React, { useCallback, useMemo } from 'react';
import { type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet, useParams } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';

import {
  getGetCurrentUserQueryKey,
  getGetUserByIdQueryKey,
  useGetCurrentUser,
  useGetUserById,
  useUpdateUser,
  useUpdateUserMidotRoles,
  useUpdateUserOffices
} from '@/api/generated';
import { usePlatformPermissions } from '@/hooks/permissions/usePlatformPermissions';
import { useUserPermissions } from '@/hooks/permissions/useUserPermissions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { useSnackbar } from '@/hooks/useSnackbar';
import { ROUTER_PATHS, SETTINGS_STEP_KEYS, toUserSettings } from '@/root/linker';
import SettingsShell from '@/views/SettingsShell/SettingsShell';

const UserSettings: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { t, i18n } = useTranslation(['users', 'settings']);
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const { data: currentUser } = useGetCurrentUser();

  const { data: user, refetch: refetchUser } = useGetUserById(userId!, { query: { enabled: !!userId } });

  const { value: { canUpdate } = {} } = useUserPermissions({
    variables: { userId: userId! }
  });

  const { value: { canAssignMidotRoles } = {} } = usePlatformPermissions();

  const { mutateAsync: updateUser } = useUpdateUser();
  const { mutateAsync: updateUserOffices } = useUpdateUserOffices();
  const { mutateAsync: updateUserRoles } = useUpdateUserMidotRoles();

  const defaultValues = useMemo(() => {
    if (!user) return {};

    return {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      fullName: [user.firstName, user.lastName].filter(Boolean).join(' ') || '',
      email: user.email || '',
      phone: user.phone || '',
      title: user.jobTitle || '',
      avatarUrl: user.avatarUrl || '',
      status: user.status || 'ACTIVE',
      preferredLocale: user.preferredLocale || 'en-US',
      preferredDateFormat: user.preferredDateFormat || 'DD/MM/YYYY',
      preferredTimezone: user.preferredTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      offices: user.offices || [],
      isMidotEmployee: user.isMidotEmployee || false,
      id: user.id || '',
      legacyId: user.legacyId || '',
      midotRoles: user.midotRoles || []
    };
  }, [user]);

  const onSave = useCallback(
    async (data: FieldValues) => {
      try {
        if (canUpdate) {
          await updateUser({
            userId: userId!,
            data: {
              fullName: data.fullName,
              jobTitle: data.title,
              legacyId: data.legacyId,
              preferredLocale: data.preferredLocale,
              preferredDateFormat: data.preferredDateFormat,
              preferredTimezone: data.preferredTimezone
            }
          });
        }
        if (data.isMidotEmployee && canAssignMidotRoles) {
          await updateUserOffices({
            userId: userId!,
            data: {
              offices: data.offices
            }
          });
          await updateUserRoles({
            userId: userId!,
            data: {
              roleIds: data.midotRoles
            }
          });
        }

        await refetchUser();

        if (userId === currentUser?.id) {
          await queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
          await queryClient.invalidateQueries({ queryKey: getGetUserByIdQueryKey(userId!) });
          if (data.preferredLocale) {
            await i18n.changeLanguage(data.preferredLocale as string);
          }
        }

        showMessage(t('settings.alerts.success'));
      } catch (error) {
        showMessage(t('settings.alerts.error'));
      }
    },
    [
      canUpdate,
      canAssignMidotRoles,
      currentUser?.id,
      i18n,
      queryClient,
      refetchUser,
      showMessage,
      t,
      updateUser,
      updateUserOffices,
      updateUserRoles,
      userId
    ]
  );

  const basicInfoRoute = useMemo(
    () => toUserSettings({ userId: userId!, stepKey: SETTINGS_STEP_KEYS.BASIC_INFO }),
    [userId]
  );

  const rolesRoute = useMemo(
    () => toUserSettings({ userId: userId!, stepKey: SETTINGS_STEP_KEYS.ACCESS_AND_ROLES }),
    [userId]
  );

  const tabs = useMemo(
    () => [
      {
        stepKey: SETTINGS_STEP_KEYS.BASIC_INFO,
        label: t('users.sections.basicInfo'),
        link: basicInfoRoute
      },
      {
        stepKey: SETTINGS_STEP_KEYS.ACCESS_AND_ROLES,
        label: t('users.sections.roles'),
        link: rolesRoute
      }
    ],
    [t, basicInfoRoute, rolesRoute]
  );

  return (
    <Stack px={5} py={2} gap={3} alignItems="start" width="100%" height="100%">
      <Typography variant="h2">{defaultValues.fullName}</Typography>
      <ReadonlyProvider readonly={!canUpdate}>
        <SettingsShell
          tabs={tabs}
          defaultValues={defaultValues}
          defaultRoute={basicInfoRoute}
          routePattern={ROUTER_PATHS.USERS_SETTINGS_GENERIC}
          onSave={onSave}
          sx={{ width: '100%' }}
        >
          <Outlet />
        </SettingsShell>
      </ReadonlyProvider>
    </Stack>
  );
};

export default UserSettings;
